import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, message, Spin } from 'antd';
import { UserOutlined, MedicineBoxOutlined, TeamOutlined, ShoppingOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const { t } = useTranslation();

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            const response = await api.get('/medicalseller/analytics');
            setAnalytics(response.data.data);
        } catch (error) {
            message.error('Failed to load analytics');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    const doctorColumns = [
        {
            title: 'Doctor Name',
            dataIndex: 'doctorName',
            key: 'doctorName',
        },
        {
            title: 'Specialization',
            dataIndex: 'specialization',
            key: 'specialization',
        },
        {
            title: 'Patients',
            dataIndex: 'patientCount',
            key: 'patientCount',
            sorter: (a, b) => a.patientCount - b.patientCount,
        },
        {
            title: 'Visits',
            dataIndex: 'visitCount',
            key: 'visitCount',
            sorter: (a, b) => a.visitCount - b.visitCount,
        },
        {
            title: 'Drugs Prescribed',
            dataIndex: 'drugsPrescribed',
            key: 'drugsPrescribed',
            sorter: (a, b) => a.drugsPrescribed - b.drugsPrescribed,
        }
    ];

    const drugColumns = [
        {
            title: 'Drug Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Times Prescribed',
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => a.count - b.count,
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <h1 style={{ fontSize: '28px', marginBottom: '24px' }}>
                {analytics?.companyName || 'Medical Seller'} Dashboard
            </h1>

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Doctors"
                            value={analytics?.summary.totalDoctors}
                            prefix={<UserOutlined />}
                            suffix={`/ ${analytics?.summary.doctorLimit}`}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Patients"
                            value={analytics?.summary.totalPatients}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Visits"
                            value={analytics?.summary.totalVisits}
                            prefix={<MedicineBoxOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Drugs Prescribed"
                            value={analytics?.summary.totalDrugsPrescribed}
                            prefix={<ShoppingOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Remaining Slots Alert */}
            {analytics?.summary.remainingDoctorSlots !== 'unlimited' && (
                <Card style={{ marginBottom: '24px', background: '#f0f5ff' }}>
                    <p style={{ margin: 0, fontSize: '16px' }}>
                        <strong>Remaining Doctor Slots:</strong> {analytics?.summary.remainingDoctorSlots}
                        {analytics?.summary.remainingDoctorSlots <= 2 && (
                            <span style={{ color: '#ff4d4f', marginLeft: '10px' }}>
                                ⚠️ You are running low on doctor slots. Contact support to increase your limit.
                            </span>
                        )}
                    </p>
                </Card>
            )}

            {/* Doctor Performance Table */}
            <Card title="Doctor Performance" style={{ marginBottom: '24px' }}>
                <Table
                    columns={doctorColumns}
                    dataSource={analytics?.doctorPerformance || []}
                    rowKey="doctorId"
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Top Prescribed Drugs */}
            <Card title="Top Prescribed Drugs">
                <Table
                    columns={drugColumns}
                    dataSource={analytics?.topPrescribedDrugs || []}
                    rowKey="name"
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        </div>
    );
};

export default Dashboard;
